import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Paperclip, Trash2, Pencil, CalendarDays } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fileUrl } from "../components/EquipmentParts";

const today = () => new Date().toISOString().slice(0, 10);

export default function DailyReports() {
  const { t } = useLang();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ user_id: "", date_from: "", date_to: "" });
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
    const { data } = await api.get("/daily-reports", { params });
    setReports(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/daily-reports", { text, attachment });
      toast.success(t("report_submitted"));
      setText(""); setAttachment(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/daily-reports/${id}`, { text: editText });
      toast.success(t("saved"));
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/daily-reports/${id}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("daily_report")}</h1>
        <p className="text-slate-400 mt-1 text-sm">{t("daily_report_hint")}</p>
      </div>

      <div className="glass rounded-2xl p-6 space-y-3" data-testid="daily-report-form">
        <Label className="text-xs uppercase tracking-widest text-slate-400">{t("today")} · {today()}</Label>
        <Textarea data-testid="daily-report-text" placeholder={t("daily_report_placeholder")} value={text} onChange={(e) => setText(e.target.value)}
          className="bg-white/5 border-white/10 min-h-[120px]" />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer">
            <Paperclip className="w-3.5 h-3.5" />{t("upload_file")}
            <input data-testid="daily-report-upload" type="file" onChange={upload} hidden />
          </label>
          {attachment && <span className="text-xs text-emerald-300 truncate max-w-[240px]">{attachment.original_filename}</span>}
          <Button data-testid="daily-report-submit" onClick={submit} disabled={busy || !text.trim()} className="ml-auto btn-glow bg-indigo-500 hover:bg-indigo-600">
            <Send className="w-4 h-4 mr-1.5" />{busy ? "…" : t("submit")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-slate-400">{t("assigned_to")}</Label>
          <Select value={filter.user_id || "all"} onValueChange={(v) => setFilter({ ...filter, user_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="report-filter-user" className="mt-1 w-[200px] bg-white/5 border-white/10"><SelectValue placeholder={t("all")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {t(u.role)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("from")}</Label>
          <Input data-testid="report-filter-from" type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("to")}</Label>
          <Input data-testid="report-filter-to" type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
        </div>
        <Button variant="ghost" data-testid="report-filter-reset" onClick={() => setFilter({ user_id: "", date_from: "", date_to: "" })} className="text-slate-300">{t("all")}</Button>
      </div>

      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.id} data-testid={`daily-report-${r.id}`} className="glass rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold">{r.user_name} <span className="text-xs text-slate-400">· {t(r.user_role || "member")}</span></div>
                <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                  <CalendarDays className="w-3 h-3" />{r.date} · {new Date(r.created_at).toLocaleTimeString()}
                  {r.updated_at && <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px]">{t("edited")}</Badge>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {r.user_id === user?.id && r.date === today() && (
                  <Button size="sm" variant="ghost" data-testid={`report-edit-${r.id}`} onClick={() => { setEditing(r.id); setEditText(r.text); }} className="h-7 w-7 p-0 text-slate-300 hover:bg-white/10">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
                {user?.role === "admin" && (
                  <Button size="sm" variant="ghost" data-testid={`report-delete-${r.id}`} onClick={() => remove(r.id)} className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            {editing === r.id ? (
              <div className="mt-3 space-y-2">
                <Textarea data-testid={`report-edit-text-${r.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} className="bg-white/5 border-white/10" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>{t("cancel")}</Button>
                  <Button size="sm" data-testid={`report-edit-save-${r.id}`} onClick={() => saveEdit(r.id)} disabled={!editText.trim()} className="bg-indigo-500 hover:bg-indigo-600">{t("save")}</Button>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-200 whitespace-pre-wrap">{r.text}</p>
            )}
            {r.attachment?.storage_path && (
              <a data-testid={`report-attachment-${r.id}`} href={fileUrl(r.attachment.storage_path)} target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10">
                <Paperclip className="w-3 h-3" />{r.attachment.original_filename}
              </a>
            )}
          </div>
        ))}
        {reports.length === 0 && <div className="glass rounded-2xl py-12 text-center text-sm text-slate-500">{t("no_data")}</div>}
      </div>
    </div>
  );
}
